"use client";

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { 
  LayoutDashboard, MessageSquare, Shield, Settings, FileText, Package, AlertOctagon, 
  ShoppingCart, FileOutput, FilePlus, RefreshCcw, HandCoins,
  Receipt, Landmark, LineChart, ReceiptText, Printer, Plus, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, Factory, ShieldAlert,
  Wallet, Users, Tags, BookOpen, PackageOpen, FileCheck, Building, Wrench, FileBarChart, FileBadge2, LogOut, CreditCard, MapPin, Layers, ClipboardCheck, BookText, Briefcase, Settings2, Menu, X, PanelLeftClose, PanelLeftOpen
, ShieldCheck, FileSpreadsheet , Clock, History } from 'lucide-react';
import LogoutButton from './LogoutButton';


function SidebarGroup({ title, groupKey, collapsed, onToggle, children }: { title: string; groupKey: string; collapsed: boolean; onToggle: (key: string) => void; children: React.ReactNode }) {
  return (
    <div className="mt-3 mb-1">
      <div 
        onClick={() => onToggle(groupKey)} 
        className="px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-white uppercase tracking-wider flex items-center justify-between cursor-pointer rounded-lg hover:bg-slate-800/60 transition select-none group"
      >
        <span>{title}</span>
        {collapsed ? <ChevronRight size={14} className="text-slate-500 group-hover:text-slate-300" /> : <ChevronDown size={14} className="text-slate-500 group-hover:text-slate-300" />}
      </div>
      {!collapsed && (
        <div className="space-y-0.5 mt-1">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const [isOpen, setIsOpen] = useState(true);
  const [isIframe, setIsIframe] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [debugStr, setDebugStr] = useState('');
  const pathname = usePathname();
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const router = require('next/navigation').useRouter();
  useEffect(() => {
    if (window.self !== window.top) {
      setIsIframe(true);
      return;
    }
    const userStr = localStorage.getItem('user');

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        let perms: string[] = [];
        
        // Failsafe for admin
        if (user.role === 'admin') {
          setPermissions(['all']);
          return;
        }

        if (typeof user.permissions === 'string') {
          try {
            perms = JSON.parse(user.permissions);
          } catch(err) {
            // If it's a string like "Read/Write" but not JSON array
            perms = [];
          }
        } else if (Array.isArray(user.permissions)) {
          perms = user.permissions;
        }
        setPermissions(perms);
      } catch(e) {}
    }
  }, []);
  
  const hasPerm = (p: string, parentGroup?: string) => {
    if (permissions.includes('all')) return true;
    if (permissions.includes(p)) return true;
    if (parentGroup && permissions.includes(parentGroup)) return true;

    if (p === 'inventory') {
      const invSub = ['inventory_iu', 'inventory_adjust', 'stocktaking', 'scrap_return', 'toner_requisitions', 'toner_requisition_form', 'tickets'];
      return invSub.some(sub => permissions.includes(sub));
    }
    if (p === 'sales') {
      const salesSub = ['sales_history', 'quotations', 'sales_orders', 'billing', 'invoices', 'receipts', 'sales_persons', 'rental_contracts'];
      return salesSub.some(sub => permissions.includes(sub));
    }
    if (p === 'purchases') {
      const purSub = ['purchases_history', 'purchase_po', 'receive_goods_history', 'receive_goods'];
      return purSub.some(sub => permissions.includes(sub));
    }
    if (p === 'master_data') {
      const mdSub = ['customers', 'products', 'vendors', 'services', 'assets'];
      return mdSub.some(sub => permissions.includes(sub));
    }

    return false;
  };


  useEffect(() => {
    if (pathname === '/finance/bank-reconcile' || pathname === '/reports/customer-balances' || pathname.startsWith('/purchase/view') || pathname.startsWith('/quotation/view')) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [pathname]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const onToggleSidebar = () => setIsOpen(prev => !prev);
    window.addEventListener("toggle-sidebar", onToggleSidebar);
    return () => window.removeEventListener("toggle-sidebar", onToggleSidebar);
  }, []);


  useEffect(() => {
    const handleResize = () => {
      const shouldBeClosed = window.location.pathname === '/finance/bank-reconcile' || 
                             window.location.pathname === '/reports/customer-balances' || 
                             window.location.pathname.startsWith('/purchase/view') || 
                             window.location.pathname.startsWith('/quotation/view');
                             
      if (window.innerWidth < 768) {
        setIsOpen(false);
        setIsMobile(true);
      } else {
        setIsOpen(!shouldBeClosed);
        setIsMobile(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    if (pathname === '/login' || pathname === '/landing' || isIframe) return null;

  return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLinkClick = () => {
    if (isMobile) setIsOpen(false);
  };

  if (pathname === '/login' || pathname === '/landing' || isIframe) return null;

  return (
    <>
      {/* Floating Toggle Button (visible when sidebar is closed) */}
      {!isOpen && (
        <button 
          onClick={() => setIsOpen(true)}
          className="fixed top-4 left-4 z-[60] p-2 bg-slate-800/90 backdrop-blur rounded-lg text-slate-300 hover:text-white shadow-[0_0_15px_rgba(0,0,0,0.5)] border border-slate-700 transition-all hover:scale-105 print:hidden"
        >
          <Menu size={24} />
        </button>
      )}

      {/* Backdrop for mobile */}
      {isOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Content */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen
        bg-slate-900 border-r border-slate-800 flex flex-col
        transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0 w-64 shadow-2xl' : '-translate-x-full w-64'}
      `}>
        
        {/* Collapse Handle on the right edge of Sidebar */}
        {isOpen && (
          <button
            onClick={() => setIsOpen(false)}
            title="ย่อแถบเมนูซ้าย"
            className="hidden md:flex absolute -right-3.5 top-14 z-50 w-7 h-7 bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white rounded-full items-center justify-center border border-slate-700 shadow-lg cursor-pointer transition-all"
          >
            <PanelLeftClose size={14} />
          </button>
        )}

        <div className="p-4 pt-2 flex flex-col gap-1 h-full overflow-y-auto w-64">
          <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur py-2 flex items-center justify-between mb-4 px-2 border-b border-slate-800/80">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg leading-none tracking-tighter">E</span>
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Express ERP</h1>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-md transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          <Link href="/" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-300 group">
            <LayoutDashboard size={18} className="group-hover:text-blue-400 transition-colors" /> <span className="font-medium">Dashboard</span>
          </Link>

          <Link href="/chat" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-300 group">
            <MessageSquare size={18} className="group-hover:text-purple-400 transition-colors" /> <span className="font-medium">แชททีม (Chat)</span>
          </Link>

          <Link href="/line-notifications" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-emerald-300 group border border-emerald-500/20 bg-emerald-950/20">
            <span className="w-4 h-4 rounded bg-[#06C755] text-white font-black text-[9px] flex items-center justify-center shrink-0">L</span>
            <span className="font-semibold text-sm">แจ้งเตือน LINE (LINE OA)</span>
            <span className="ml-auto w-2 h-2 rounded-full bg-[#06C755] animate-pulse"></span>
          </Link>

          
          {hasPerm('dashboard') && (<>
          <div className="mt-4 mb-1 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Executive (ผู้บริหาร)</div>
          <Link href="/approvals" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group relative">
             <CheckCircle2 size={16} className="text-emerald-500 group-hover:text-emerald-400 transition-colors" /> 
             <span className="font-semibold text-emerald-100">อนุมัติเอกสารหลัก</span>
             <span className="absolute right-3 w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          </Link>
          </>)}

          {hasPerm('sales') && (<SidebarGroup title="Sales (ขาย)" groupKey="sales" collapsed={!!collapsedSections['sales']} onToggle={toggleSection}>
          <Link href="/quotations" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileText size={16} className="group-hover:text-amber-400 transition-colors" /> ประวัติใบเสนอราคา
          </Link>
          <Link href="/sales-history/iv" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileText size={16} className="group-hover:text-blue-400 transition-colors" /> ประวัติบิลขาย (IV)
          </Link>
          <Link href="/sales-history/ir" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileText size={16} className="group-hover:text-red-400 transition-colors" /> ประวัติใบลดหนี้ (IR)
          </Link>
          <Link href="/sales-orders" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <ShoppingCart size={16} className="group-hover:text-emerald-400 transition-colors" /> ใบสั่งขาย (SO) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500"></span>
          </Link>
          <Link href="/invoice" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Receipt size={16} className="group-hover:text-blue-400 transition-colors" /> เปิดบิลขาย (IV)
          </Link>
          <Link href="/billing" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FilePlus size={16} className="group-hover:text-orange-400 transition-colors" /> ใบแจ้งหนี้/วางบิล (BL)
          </Link>
          <Link href="/receipt" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <HandCoins size={16} className="group-hover:text-purple-400 transition-colors" /> รับชำระหนี้ (RE)
          </Link>
          <Link href="/receipt-history/re" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileText size={16} className="group-hover:text-purple-400 transition-colors" /> ประวัติรับชำระเงิน (RE)
          </Link>
          <Link href="/receipt-history/rf" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileText size={16} className="group-hover:text-purple-400 transition-colors" /> ประวัติรับชำระเงิน (RF)
          </Link>
          <Link href="/salesman" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Users size={16} className="group-hover:text-cyan-400 transition-colors" /> พนักงานขาย <span className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
          </Link>

                    </SidebarGroup>)}

          {hasPerm('scrap_return', 'inventory') && (<SidebarGroup title="Scrap Return (ซากสินค้า)" groupKey="scrap" collapsed={!!collapsedSections['scrap']} onToggle={toggleSection}>
            <Link href="/scrap-return?tab=pending" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Clock size={16} className="group-hover:text-orange-400 transition-colors" /> 📊 ซากค้างเก็บ (Pending)
            </Link>
            <Link href="/scrap-return?tab=create" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Package size={16} className="group-hover:text-emerald-400 transition-colors" /> 📝 บันทึกรับคืนซากใหม่
            </Link>
            <Link href="/scrap-return?tab=history" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <History size={16} className="group-hover:text-blue-400 transition-colors" /> 📜 ประวัติรับคืนซาก
            </Link>
          </SidebarGroup>)}
          
          {hasPerm('inventory') && (<SidebarGroup title="Inventory (คลังสินค้า)"
 groupKey="inventory" collapsed={!!collapsedSections['inventory']} onToggle={toggleSection}>
          {hasPerm('toner_requisition_form', 'inventory') && (
            <Link href="/inventory/toner-requisitions/new" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Plus size={16} className="text-purple-400 group-hover:text-purple-300 transition-colors" /> ➕ ฟอร์มขอเบิกผงหมึก & อะไหล่ <span className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-400"></span>
            </Link>
          )}
          {hasPerm('toner_requisitions', 'inventory') && (
            <Link href="/inventory/toner-requisitions" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Printer size={16} className="group-hover:text-purple-400 transition-colors" /> 🖨️ ประวัติเบิกผงหมึก & อะไหล่ <span className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-500"></span>
            </Link>
          )}
          {hasPerm('tickets', 'inventory') && (
            <Link href="/tickets" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Wrench size={16} className="group-hover:text-orange-400 transition-colors" /> 🛠️ ฟอร์มแจ้งเรื่อง & แจ้งซ่อม <span className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-500"></span>
            </Link>
          )}
          {hasPerm('inventory_iu', 'inventory') && (
            <Link href="/inventory" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <PackageOpen size={16} className="group-hover:text-orange-400 transition-colors" /> สินค้าคงเหลือ (IU)
            </Link>
          )}
          {hasPerm('inventory_adjust', 'inventory') && (
            <Link href="/inventory/manual-adjust" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Settings2 size={16} className="group-hover:text-red-400 transition-colors" /> ปรับปรุงยอดสินค้า (IU) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-500"></span>
            </Link>
          )}
          {hasPerm('stocktaking', 'inventory') && (
            <Link href="/inventory/stocktaking" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <ClipboardCheck size={16} className="group-hover:text-yellow-400 transition-colors" /> ใบตรวจนับสต็อก
            </Link>
          )}

                    

                    </SidebarGroup>)}
          {hasPerm('purchases') && (<SidebarGroup title="Purchase (ระบบซื้อ)" groupKey="purchase" collapsed={!!collapsedSections['purchase']} onToggle={toggleSection}>
          <Link href="/purchases" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileText size={16} className="group-hover:text-emerald-500 transition-colors" /> ประวัติใบสั่งซื้อ
          </Link>
          <Link href="/purchase" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <ShoppingCart size={16} className="group-hover:text-emerald-500 transition-colors" /> ใบสั่งซื้อ (PO/RR)
          </Link>
          <Link href="/receive-goods/history" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileText size={16} className="group-hover:text-emerald-500 transition-colors" /> ประวัติการรับสินค้า RR
          </Link>
          <Link href="/receive-goods" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <PackageOpen size={16} className="group-hover:text-emerald-400 transition-colors" /> รับสินค้าเข้าสโตร์ (RR) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          </Link>

                    

                    </SidebarGroup>)}
          {hasPerm('master_data') && (<SidebarGroup title="Master Data (ฐานข้อมูล)" groupKey="master" collapsed={!!collapsedSections['master']} onToggle={toggleSection}>
          <Link href="/customers" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Users size={16} className="group-hover:text-blue-300 transition-colors" /> ทะเบียนลูกค้า
          </Link>

          <Link href="/reports/customer-transactions" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <BookText size={16} className="group-hover:text-cyan-400 transition-colors" /> ความเคลื่อนไหว (รายเอกสาร)
          </Link>
          <Link href="/products" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Tags size={16} className="group-hover:text-pink-400 transition-colors" /> ทะเบียนสินค้า (แก้ไขราคา)
          </Link>
          <Link href="/services" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Wrench size={16} className="group-hover:text-cyan-400 transition-colors" /> สินค้าบริการ (Services) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
          </Link>
          <Link href="/assets" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Building size={16} className="group-hover:text-indigo-400 transition-colors" /> ทะเบียนสินทรัพย์ (Assets) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
          </Link>

                    

                    </SidebarGroup>)}
          {(hasPerm('finance') || hasPerm('petty_cash') || hasPerm('bank_reconcile') || hasPerm('deposit') || hasPerm('profit') || hasPerm('retention')) && (
            <SidebarGroup title="Finance (การเงิน)" groupKey="finance" collapsed={!!collapsedSections['finance']} onToggle={toggleSection}>
          {(hasPerm('finance') || hasPerm('petty_cash')) && (
          <Link href="/expenses/petty-cash" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <ReceiptText size={16} className="group-hover:text-green-400 transition-colors" /> ใบขอเบิกเงินสดย่อย
          </Link>
          )}
          {hasPerm('finance') && (
          <Link href="/expenses/petty-cash/approval" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <CheckCircle2 size={16} className="group-hover:text-blue-400 transition-colors" /> อนุมัติเบิกเงินสดย่อย
          </Link>
          )}
          {hasPerm('finance') && (
          <Link href="/accounts" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <BookOpen size={16} className="group-hover:text-amber-500 transition-colors" /> ผังบัญชี (Chart)
          </Link>
          )}
          {hasPerm('finance') && (
          <Link href="/finance" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Landmark size={16} className="group-hover:text-yellow-500 transition-colors" /> งบการเงินรวม
          </Link>
          )}
          {(hasPerm('finance') || hasPerm('profit')) && (
          <>
          <Link href="/reports/ar-reconcile" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <ShieldCheck size={16} className="group-hover:text-fuchsia-500 transition-colors" /> กระทบยอด ลูกหนี้ / GL <span className="ml-auto w-1.5 h-1.5 rounded-full bg-fuchsia-500"></span>
          </Link>
          <Link href="/reports/profit" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <LineChart size={16} className="group-hover:text-emerald-400 transition-colors" /> วิเคราะห์กำไร
          </Link>
          <Link href="/reports/profit?tab=product_profit" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <PackageOpen size={16} className="group-hover:text-cyan-400 transition-colors" /> ซื้อมา-ขายไป (ตามผู้จำหน่าย) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
          </Link>
          </>
          )}
          {(hasPerm('finance') || hasPerm('bank_reconcile')) && (
          <Link href="/finance/bank-reconcile" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <RefreshCcw size={16} className="group-hover:text-cyan-400 transition-colors" /> กระทบยอด Bank (Reconcile)
          </Link>
          )}
          {(hasPerm('finance') || hasPerm('retention')) && (
          <Link href="/finance/retention" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <ShieldCheck size={16} className="group-hover:text-purple-400 transition-colors" /> เงินค้ำสัญญาราชการ
          </Link>
          )}
          {(hasPerm('finance') || hasPerm('deposit')) && (
          <Link href="/finance/deposit" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Wallet size={16} className="group-hover:text-amber-400 transition-colors" /> รับเงินมัดจำ
          </Link>
          )}
          {hasPerm('finance') && (
          <Link href="/expenses" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <ReceiptText size={16} className="group-hover:text-rose-400 transition-colors" /> เบิกเงินสดย่อย (PV)
          </Link>
          )}

                    

                    </SidebarGroup>)}
          {(hasPerm('accounting') || hasPerm('ar_balances_24') || hasPerm('ar')) && (
            <SidebarGroup title="Finance (บัญชี)" groupKey="accounting" collapsed={!!collapsedSections['accounting']} onToggle={toggleSection}>
          
          {hasPerm('accounting') && (<>
          <Link href="/taxes/vat" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <ReceiptText size={16} className="group-hover:text-blue-500 transition-colors" /> ภาษีมูลค่าเพิ่ม (VAT) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500"></span>
          </Link>
          <Link href="/taxes/wht" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileBadge2 size={16} className="group-hover:text-purple-500 transition-colors" /> ภาษีหัก ณ ที่จ่าย (นำส่ง) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-500"></span>
          </Link>
          <Link href="/taxes/wht-deducted" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileBadge2 size={16} className="group-hover:text-orange-500 transition-colors" /> ภาษีถูกหัก ณ ที่จ่าย (ถูกหัก) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-500"></span>
          </Link>
          <Link href="/reports/vat-reconcile" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <FileSpreadsheet size={16} className="group-hover:text-amber-500 transition-colors" /> กระทบยอด ภ.พ.30 / GL <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          </Link>
          <Link href="/finance/gl" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <BookText size={16} className="group-hover:text-indigo-500 transition-colors" /> สมุดรายวันทั่วไป (GL) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
          </Link>
          <Link href="/journals" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <BookText size={16} className="group-hover:text-violet-500 transition-colors" /> สมุดรายวัน (GL) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-violet-500"></span>
          </Link>
          </>)}

          {(hasPerm('ar') || hasPerm('accounting')) && (
          <Link href="/ar-outstanding" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <Briefcase size={16} className="group-hover:text-rose-500 transition-colors" /> ลูกหนี้คงค้าง (AR) <span className="ml-auto w-1.5 h-1.5 rounded-full bg-rose-500"></span>
          </Link>
          )}
          
          {hasPerm('ar_balances_24') && (
          <Link href="/reports/customer-balances" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
             <BookOpen size={16} className="group-hover:text-emerald-400 transition-colors" /> ยอดสะสมลูกค้า 24 งวด
          </Link>
          )}

          </SidebarGroup>)}
          <div className="mt-auto pt-4 pb-4 border-t border-slate-800">
             {hasPerm('settings') && (<>
               <Link href="/settings/roles" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
                 <Briefcase size={16} className="group-hover:text-blue-400 transition-colors" /> จัดการกลุ่มตำแหน่ง
               </Link>
               <Link href="/settings/users" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
                 <Shield size={16} className="group-hover:text-emerald-400 transition-colors" /> จัดการผู้ใช้งาน
               </Link>
             </>)}
             {hasPerm('master_data') && (
             <Link href="/customers" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Users size={16} className="group-hover:text-blue-400 transition-colors" /> ข้อมูลลูกค้า
             </Link>
             )}
             {hasPerm('sales') && (
             <Link href="/rentals" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <FilePlus size={16} className="group-hover:text-orange-400 transition-colors" /> สัญญาเช่า & คุมงวด
             </Link>
             )}
             {hasPerm('settings') && (
             <Link href="/settings" onClick={handleLinkClick} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800 rounded-lg transition text-slate-400 text-sm group">
               <Settings size={16} className="group-hover:text-slate-300 transition-colors" /> ตั้งค่าระบบ
             </Link>
             )}
          <LogoutButton />
          </div>
        </div>
      </aside>
      
      {/* Spacer to push main content when sidebar is open on desktop */}
      <div className={`hidden md:block transition-all duration-300 ease-in-out ${isOpen ? 'w-64 min-w-[16rem]' : 'w-0 min-w-0'}`} />
    </>
  );
}
